/**
 * Réglages (menu Profil du dock) : langue, thème, zoom (Bureau, Mobile), filiale par défaut, cartes de l'accueil (et mode d'affichage), favoris du dock,
 * messagerie dans le dock (si l'utilisateur a droit à l'outil), Scribe dans le dock (idem), téléphone (softphone du pilote : bouton Téléphone du
 * dock ; option grisée hors du groupe du softphone), compte (e-mail, profils d'accès, filiales autorisées), aide.
 * Enregistrement automatique (préférences serveur).
 */
import React from 'react';
import { useLocation } from 'react-router-dom';
import { FILIALES, LANGUES, PREREGLAGES_REPARTITION, RESSOURCE_MESSAGERIE, RESSOURCE_SCRIBE, TOUTES_FILIALES, WIDGETS_ACCUEIL, ZOOMS, aDesModes, modeWidget, type Langue, type Theme, type Zoom } from '@agora/shared';
import { Button, ChoiceList, Icon, SegmentedControl, ShapeFlag, Toggle, WorldGlobe } from '../../design';
import { useFormats, useMoi, usePreferences, useT } from '../../socle';
import { useSocle } from '../contexte';
import { ecranZoom } from '../zoom';
import { nomFiliale } from '../shell/Entete';
import { groupeSoftphone, optionSoftphone } from '../telephonie/softphone/acces';
import { ChoixModeCarte, cheminNoeud } from './Accueil';

function Carte({ id, titre, children }: { id?: string; titre: string; children: React.ReactNode }) {
  return (
    <section id={id} className="ag-glass-card agx-carte" aria-label={titre}>
      <h2 className="agx-titre-carte">{titre}</h2>
      {children}
    </section>
  );
}

export function Reglages() {
  const { t, lib } = useT();
  const moi = useMoi();
  const { preferences, modifier } = usePreferences();
  const { filialesAutorisees, noeuds, config } = useSocle();
  // Téléphone (softphone) : option grisée hors du groupe du softphone de l'environnement, absente sans instance Connect.
  const telephone = optionSoftphone(config, moi.groupes);
  const formats = useFormats();
  const ecran = ecranZoom();
  const { hash } = useLocation();
  React.useEffect(() => {
    if (hash) document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [hash]);

  const choixFiliales = [...(filialesAutorisees.length > 1 ? [TOUTES_FILIALES] : []), ...FILIALES.filter((f) => filialesAutorisees.includes(f.code)).map((f) => f.code)];
  // Chaque langue est écrite dans sa propre langue (« Deutsch »), `lang` renseigné.
  const langues = [
    { value: 'auto', label: t('reglages.langueAuto') },
    ...LANGUES.map((l) => ({ value: l, label: t(`langues.${l}`), lang: l })),
  ];

  return (
    <>
      <div className="ag-pagehead">
        <div>
          <h1>{t('reglages.titre')}</h1>
          <p className="agx-sous-titre">{t('reglages.enregistrementAuto')}</p>
        </div>
      </div>
      <div className="agx-grille">
        <Carte titre={t('reglages.langue')}>
          {/* « Automatique » + les langues : plus de 6 choix, donc liste de choix (un contrôle segmenté de 7 options ne
              tenait pas dans la carte : « Automatique » passait sous la pastille de l'option choisie). */}
          <ChoiceList
            ariaLabel={t('reglages.langue')} check options={langues} value={preferences.langue || 'auto'}
            onChange={(v) => modifier((p) => ({ ...p, langue: v === 'auto' ? undefined : (v as Langue) }))}
          />
          {preferences.langue ? null : <p className="agx-vide">{t('reglages.filialeDefautAide')}</p>}
        </Carte>

        <Carte titre={t('reglages.theme')}>
          <SegmentedControl
            ariaLabel={t('reglages.theme')} value={preferences.theme}
            options={(['clair', 'sombre', 'systeme'] as Theme[]).map((v) => ({ value: v, label: t(`reglages.${v}`) }))}
            onChange={(v) => modifier((p) => ({ ...p, theme: v as Theme }))}
          />
        </Carte>

        {/* Zoom de l'affichage, à part sur ordinateur et sur téléphone (core/zoom.ts) ; appliqué dès le choix. */}
        <Carte id="zoom" titre={t('reglages.zoom')}>
          <p className="agx-vide">{t('reglages.zoomAide')}</p>
          {(['bureau', 'mobile'] as const).map((e) => {
            const nom = t(`reglages.zoomEcran.${e}`);
            return (
              <div key={e} className="agx-reglage">
                <p className="agx-sous-titre-carte">{e === ecran ? t('reglages.zoomCetAppareil', { ecran: nom }) : nom}</p>
                <SegmentedControl
                  ariaLabel={t('reglages.zoomNom', { ecran: nom })} value={String(preferences.zoom[e])}
                  options={ZOOMS.map((z) => ({ value: String(z), label: formats.nombre(z / 100, { style: 'percent' }) }))}
                  onChange={(v) => modifier((p) => ({ ...p, zoom: { ...p.zoom, [e]: Number(v) as Zoom } }))}
                />
              </div>
            );
          })}
        </Carte>

        <Carte titre={t('reglages.filialeDefaut')}>
          <p className="agx-vide">{t('reglages.filialeDefautAide')}</p>
          {/* Liste de choix du design (Radix RadioGroup) : flèches, Début / Fin, un seul arrêt de tabulation. */}
          <ChoiceList
            ariaLabel={t('reglages.filialeDefaut')} check
            value={preferences.filiale || (filialesAutorisees.length > 1 ? TOUTES_FILIALES : filialesAutorisees[0])}
            options={choixFiliales.map((code) => {
              const f = FILIALES.find((x) => x.code === code);
              return {
                value: code, label: nomFiliale(code, t),
                leading: f ? <ShapeFlag country={f.pays} size={20} /> : <WorldGlobe size={20} />,
                trailing: f ? <span className="ag-mono agx-code">{code}</span> : undefined,
              };
            })}
            onChange={(code) => modifier((p) => ({ ...p, filiale: code }))}
          />
        </Carte>

        <Carte titre={t('reglages.cartesAccueil')}>
          {WIDGETS_ACCUEIL.map((id) => {
            const w = preferences.accueil.widgets.find((x) => x.id === id);
            return (
              <React.Fragment key={id}>
                <Toggle label={t(`accueil.widgets.${id}`)} checked={!!w?.visible}
                  help={aDesModes(id) ? t(`accueil.modesAide.${id}.${modeWidget(preferences, id)}`) : undefined}
                  onChange={(v) => modifier((p) => ({ ...p, accueil: { widgets: p.accueil.widgets.map((x) => (x.id === id ? { ...x, visible: v } : x)) } }))} />
                {aDesModes(id) ? <div className="agx-mode-carte"><ChoixModeCarte id={id} /></div> : null}
              </React.Fragment>
            );
          })}
        </Carte>

        {telephone !== 'absente' ? (
          <Carte id="telephone" titre={t('service.telephonie.softphone.titre')}>
            <Toggle
              label={t('service.telephonie.softphone.activer')} checked={telephone === 'disponible' && !!preferences.softphone}
              disabled={telephone === 'grisee'}
              help={telephone === 'grisee'
                ? t('service.telephonie.softphone.reserve', { groupe: groupeSoftphone(config) })
                : t('service.telephonie.softphone.activerAide')}
              onChange={(v) => modifier((p) => ({ ...p, softphone: v }))}
            />
          </Carte>
        ) : null}

        <Carte titre={t('reglages.favoris')}>
          {preferences.favoris.length === 0 ? <p className="agx-vide">{t('reglages.aucunFavori')}</p> : (
            <div className="agx-liste">
              {preferences.favoris.map((id) => (
                <div key={id} className="ag-menu-item agx-ligne">
                  <Icon name={noeuds.get(id)?.icone || 'grid'} size={18} />
                  <span className="ag-menu-label">{lib(id)} <small className="agx-code">{cheminNoeud(id, lib)}</small></span>
                  <Button size="sm" variant="quiet" icon="minus" onClick={() => modifier((p) => ({ ...p, favoris: p.favoris.filter((x) => x !== id) }))}>
                    {t('dock.retirer')}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </Carte>

        {noeuds.get(RESSOURCE_MESSAGERIE)?.droits.lire ? (
          <Carte id="messagerie" titre={t('reglages.messagerie')}>
            <Toggle label={t('reglages.messagerieDock')} help={t('reglages.messagerieAide')} checked={preferences.messagerie}
              onChange={(v) => modifier((p) => ({ ...p, messagerie: v }))} />
            {/* Répartition liste | message de l'écran (aussi réglable en faisant glisser leur séparation). */}
            <div className="agx-reglage">
              <p className="agx-sous-titre-carte">{t('reglages.messagerieRepartition')}</p>
              <SegmentedControl
                ariaLabel={t('reglages.messagerieRepartition')}
                value={(Object.keys(PREREGLAGES_REPARTITION) as (keyof typeof PREREGLAGES_REPARTITION)[]).find((k) => PREREGLAGES_REPARTITION[k] === preferences.messagerieRepartition) ?? ''}
                options={(Object.keys(PREREGLAGES_REPARTITION) as (keyof typeof PREREGLAGES_REPARTITION)[]).map((k) => ({ value: k, label: t(`reglages.repartition.${k}`) }))}
                onChange={(v) => modifier((p) => ({ ...p, messagerieRepartition: PREREGLAGES_REPARTITION[v as keyof typeof PREREGLAGES_REPARTITION] }))}
              />
              <p className="agx-vide">{t('reglages.messagerieRepartitionAide', { liste: preferences.messagerieRepartition, message: 100 - preferences.messagerieRepartition })}</p>
            </div>
          </Carte>
        ) : null}

        {/* Scribe (enregistreur de réunions) : bouton du dock ; il reste affiché pendant une réunion en cours. */}
        {noeuds.get(RESSOURCE_SCRIBE)?.droits.lire ? (
          <Carte id="scribe" titre={lib(RESSOURCE_SCRIBE)}>
            <Toggle label={t('reglages.scribeDock')} help={t('reglages.scribeAide')} checked={preferences.scribe}
              onChange={(v) => modifier((p) => ({ ...p, scribe: v }))} />
          </Carte>
        ) : null}

        <Carte id="compte" titre={t('reglages.compte')}>
          <dl className="agx-dl">
            <dt>{t('connexion.email')}</dt>
            <dd>{[moi.prenom, moi.nom].filter(Boolean).join(' ')} · {moi.email}</dd>
            <dt>{t('reglages.groupes')}</dt>
            <dd>{moi.groupes.length ? moi.groupes.map((g) => <span key={g} className="ag-mono agx-puce">{g}</span>) : '—'}</dd>
            <dt>{t('reglages.filialesAutorisees')}</dt>
            <dd className="agx-drapeaux">
              {FILIALES.filter((f) => moi.filiales.includes(f.code)).map((f) => (
                <span key={f.code} className="agx-puce" title={nomFiliale(f.code, t)}><ShapeFlag country={f.pays} size={18} /> <span className="ag-mono">{f.code}</span></span>
              ))}
            </dd>
          </dl>
        </Carte>

        <Carte id="aide" titre={t('profil.aide')}>
          <p className="agx-vide">{t('connexion.aide')}</p>
          <p className="agx-vide ag-mono">{t('module.guide')}</p>
        </Carte>
      </div>
    </>
  );
}
